// ==================== LOGIN PAGE ====================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, LogIn, AlertCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Checkbox } from '../../components/ui/Checkbox';
import { ROUTES } from '../../constants';
import { useAuth } from '../../contexts';
import { loginFormSchema, type LoginFormData } from '../../utils/form-validations';
import { fadeInUp, staggerContainer } from '../../utils/animations';

// ==================== MAIN COMPONENT ====================

const LoginPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, error, clearError, isLoading } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    clearError();
    await login({ email: data.email, password: data.password });
    // Navigation is handled by AuthContext based on user role/status
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
      className="animate-fade-in"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="text-center mb-8">
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white mb-2">
          Welcome Back
        </h1>
        <p className="text-(--color-muted-gray)">
          Sign in to your account to continue
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* General Error */}
        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -10, height: 0 }}
              transition={{ duration: 0.3 }}
              className="flex items-start gap-3 p-4 rounded-md bg-red-500/10 border border-red-500/30"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-500">Authentication Failed</p>
                <p className="text-sm text-red-400 mt-0.5">{error}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Email */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            error={errors.email?.message}
            leftIcon={<Mail size={18} />}
            autoComplete="email"
            {...register('email')}
          />
        </motion.div>

        {/* Password */}
        <motion.div variants={fadeInUp}>
          <Input
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter your password"
            error={errors.password?.message}
            leftIcon={<Lock size={18} />}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            }
            autoComplete="current-password"
            {...register('password')}
          />
        </motion.div>

        {/* Remember Me & Forgot Password */}
        <motion.div variants={fadeInUp} className="flex items-center justify-between">
          <Checkbox
            label="Remember me"
            {...register('rememberMe')}
          />
          <Link
            to={ROUTES.AUTH.FORGOT_PASSWORD}
            className="text-sm text-(--color-signal-orange) hover:text-(--color-orange-light) transition-colors"
          >
            Forgot password?
          </Link>
        </motion.div>

        {/* Submit Button */}
        <motion.div variants={fadeInUp}>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            isLoading={isLoading}
            leftIcon={<LogIn size={18} />}
          >
            Sign In
          </Button>
        </motion.div>
      </form>

      {/* Divider */}
      <div className="relative my-8">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-(--color-border-gray)" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-4 bg-(--color-midnight-navy) text-(--color-muted-gray)">
            or
          </span>
        </div>
      </div>

      {/* Register Link */}
      <motion.p 
        variants={fadeInUp}
        className="text-center text-sm text-(--color-muted-gray)"
      >
        Don't have an account?{' '}
        <Link
          to={ROUTES.AUTH.REGISTER}
          className="text-(--color-signal-orange) hover:text-(--color-orange-light) font-medium transition-colors"
        >
          Create one
        </Link>
      </motion.p>
    </motion.div>
  );
};

export { LoginPage };
